import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Clock, 
  Users, 
  CheckCircle, 
  BookOpen,
  Wrench,
  Target,
  ArrowRight
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";

const courseData = {
  "autocad-api": {
    name: "AutoCAD APIs",
    tagline: "Master AutoCAD Automation & Customization",
    intro: "Learn to automate AutoCAD using .NET API, AutoLISP, and ObjectARX. Build custom commands, plugins, and powerful automation tools that dramatically increase productivity.",
    duration: "2-3 months",
    level: "Intermediate to Advanced",
    skills: [
      "AutoCAD .NET API fundamentals and architecture",
      "Creating custom commands and tool palettes",
      "Working with entities, blocks, and layers programmatically",
      "AutoLISP scripting for quick automation",
      "Building standalone applications that interact with AutoCAD",
      "Database manipulation and external data integration",
      "Deploying and distributing custom plugins"
    ],
    tools: ["C#", "VB.NET", "AutoLISP", "Python", "ObjectARX", "Visual Studio"],
    curriculum: [
      {
        title: "Module 1: API Fundamentals",
        topics: ["AutoCAD object model", "Development environment setup", "Your first plugin", "Debugging techniques"]
      },
      {
        title: "Module 2: Working with Entities",
        topics: ["Creating and modifying entities", "Selection sets", "Entity properties", "Transformations"]
      },
      {
        title: "Module 3: Blocks & References",
        topics: ["Block definitions", "Block references", "Attributes", "Dynamic blocks via API"]
      },
      {
        title: "Module 4: User Interface",
        topics: ["Custom commands", "Palettes", "Dialogs and forms", "Ribbon customization"]
      },
      {
        title: "Module 5: AutoLISP Integration",
        topics: ["LISP fundamentals", "AutoLISP functions", "Calling LISP from .NET", "Migration strategies"]
      },
      {
        title: "Module 6: Advanced Topics",
        topics: ["Database connectivity", "External file handling", "Batch processing", "Deployment"]
      }
    ],
    projects: [
      "Automated drawing generator from Excel data",
      "Custom block library manager",
      "Batch processing tool for multiple drawings"
    ],
    audience: ["CAD administrators", "Design engineers", "Drafters seeking automation skills", "Software developers entering CAD"]
  },
  "solidworks-api": {
    name: "SolidWorks APIs",
    tagline: "Automate SolidWorks with Professional API Development",
    intro: "Master SolidWorks API programming to automate design tasks, build custom add-ins, and create powerful standalone applications using C#, VB.NET, and VBA.",
    duration: "2-3 months",
    level: "Intermediate to Advanced",
    skills: [
      "SolidWorks API architecture and object model",
      "Building add-ins and macros",
      "Part, assembly, and drawing automation",
      "Feature manipulation programmatically",
      "Custom property and configuration management",
      "Task pane and PropertyManager page creation",
      "Integration with PDM systems"
    ],
    tools: ["C#", "VB.NET", "VBA", "Python", "Visual Studio", "SolidWorks API SDK"],
    curriculum: [
      {
        title: "Module 1: Getting Started",
        topics: ["API overview", "Development setup", "Recording macros", "Object model navigation"]
      },
      {
        title: "Module 2: Document Manipulation",
        topics: ["Creating parts", "Assembly automation", "Drawing views", "File operations"]
      },
      {
        title: "Module 3: Feature Programming",
        topics: ["Sketches via API", "Feature creation", "Feature modification", "Pattern and mirror"]
      },
      {
        title: "Module 4: Add-in Development",
        topics: ["Add-in architecture", "Menu and toolbar", "Task pane integration", "Event handling"]
      },
      {
        title: "Module 5: Data Management",
        topics: ["Custom properties", "Configurations", "BOM extraction", "PDM integration basics"]
      },
      {
        title: "Module 6: Advanced Automation",
        topics: ["Batch operations", "External data import", "Report generation", "Deployment strategies"]
      }
    ],
    projects: [
      "Parametric part generator from specifications",
      "Automated BOM export tool",
      "Custom design configurator add-in"
    ],
    audience: ["Mechanical engineers", "Product designers", "CAD administrators", "Manufacturing automation specialists"]
  },
  "nx-open": {
    name: "NX Open",
    tagline: "Master Siemens NX Customization & Automation",
    intro: "Learn NX Open API for comprehensive automation and customization of Siemens NX. Build journals, custom applications, and integrate with enterprise systems using C++, C#, and Python.",
    duration: "3-4 months",
    level: "Intermediate to Advanced",
    skills: [
      "NX Open architecture and programming model",
      "Journal recording and editing",
      "Part and assembly automation",
      "Custom dialog creation with Block UI Styler",
      "Menu and toolbar customization",
      "Knowledge Fusion integration",
      "Teamcenter integration basics"
    ],
    tools: ["C++", "C#", "Python", "VB.NET", "NX Open API", "Block UI Styler"],
    curriculum: [
      {
        title: "Module 1: NX Open Fundamentals",
        topics: ["Architecture overview", "Journal recording", "Development environment", "Basic programming"]
      },
      {
        title: "Module 2: Part Modeling Automation",
        topics: ["Sketch creation", "Feature operations", "Expressions and parameters", "Part families"]
      },
      {
        title: "Module 3: Assembly Programming",
        topics: ["Component operations", "Constraints", "Assembly structure", "Interference checking"]
      },
      {
        title: "Module 4: User Interface",
        topics: ["Block UI Styler", "Custom dialogs", "Menu customization", "Application integration"]
      },
      {
        title: "Module 5: Drafting Automation",
        topics: ["Drawing creation", "View operations", "Dimensions and annotations", "Templates"]
      },
      {
        title: "Module 6: Enterprise Integration",
        topics: ["Teamcenter basics", "Batch processing", "External data", "Deployment"]
      }
    ],
    projects: [
      "Automated part family generator",
      "Custom design checker tool",
      "Batch drawing creation utility"
    ],
    audience: ["NX users seeking automation", "Aerospace and automotive engineers", "Manufacturing engineers", "PLM specialists"]
  },
  "eyeshot-api": {
    name: "devDept Eyeshot APIs",
    tagline: "Build Custom CAD Applications with Eyeshot",
    intro: "Learn to create professional CAD applications using devDept Eyeshot library. Build 3D viewers, editors, and custom design tools for .NET Windows applications.",
    duration: "2-3 months",
    level: "Intermediate to Advanced",
    skills: [
      "Eyeshot architecture and components",
      "3D viewport creation and configuration",
      "Entity creation and manipulation",
      "File format import/export (STEP, IGES, DWG)",
      "Selection and editing tools",
      "Custom rendering and visualization",
      "CAM integration basics"
    ],
    tools: ["C#", ".NET Framework", ".NET Core", "WPF", "WinForms", "Visual Studio"],
    curriculum: [
      {
        title: "Module 1: Eyeshot Fundamentals",
        topics: ["Library overview", "Viewport setup", "Navigation controls", "Basic entities"]
      },
      {
        title: "Module 2: Geometry Creation",
        topics: ["2D entities", "3D solids", "Surfaces", "Mesh operations"]
      },
      {
        title: "Module 3: File Operations",
        topics: ["DWG/DXF support", "STEP/IGES import", "Custom file formats", "Data exchange"]
      },
      {
        title: "Module 4: User Interaction",
        topics: ["Selection tools", "Editing operations", "Snap and constraints", "Custom actions"]
      },
      {
        title: "Module 5: Visualization",
        topics: ["Materials and textures", "Lighting", "Rendering modes", "Screenshots and export"]
      },
      {
        title: "Module 6: Advanced Applications",
        topics: ["CAM toolpath display", "Measurement tools", "Custom viewers", "Performance optimization"]
      }
    ],
    projects: [
      "Custom 3D model viewer application",
      "Simple CAD editor with basic tools",
      "STEP file viewer with measurements"
    ],
    audience: ["Software developers", "CAD application developers", "Engineering software companies", ".NET developers entering CAD"]
  },
  "inventor-api": {
    name: "Inventor APIs",
    tagline: "Automate Autodesk Inventor Like a Pro",
    intro: "Master Inventor API programming for automation and customization. Build iLogic rules, add-ins, and custom applications using VB.NET, C#, and Python.",
    duration: "2-3 months",
    level: "Intermediate to Advanced",
    skills: [
      "Inventor API object model",
      "iLogic rules and forms",
      "Add-in development",
      "Part and assembly automation",
      "Drawing automation",
      "Vault integration basics",
      "External data connectivity"
    ],
    tools: ["C#", "VB.NET", "iLogic", "Python", "Visual Studio", "Inventor API SDK"],
    curriculum: [
      {
        title: "Module 1: API Basics",
        topics: ["Object model overview", "iLogic introduction", "Recording code", "Development setup"]
      },
      {
        title: "Module 2: iLogic Mastery",
        topics: ["Rules and triggers", "Forms", "External rules", "Multi-value parameters"]
      },
      {
        title: "Module 3: Part Automation",
        topics: ["Feature creation", "Parameters", "iParts and iFeatures", "Design automation"]
      },
      {
        title: "Module 4: Assembly Programming",
        topics: ["Component placement", "Constraints", "iAssemblies", "BOM manipulation"]
      },
      {
        title: "Module 5: Add-in Development",
        topics: ["Add-in architecture", "UI customization", "Event handling", "Deployment"]
      },
      {
        title: "Module 6: Integration",
        topics: ["External data", "Vault basics", "Batch processing", "Report generation"]
      }
    ],
    projects: [
      "Configurator using iLogic forms",
      "Automated drawing generator",
      "Custom BOM export tool"
    ],
    audience: ["Inventor users", "Design engineers", "CAD administrators", "Product configurator developers"]
  },
  "cad-automation": {
    name: "CAD Automation",
    tagline: "Cross-Platform CAD Development Expertise",
    intro: "Comprehensive course covering automation across multiple CAD platforms. Learn common patterns, best practices, and techniques for building robust automation solutions.",
    duration: "3-4 months",
    level: "Advanced",
    skills: [
      "Cross-platform development strategies",
      "Common automation patterns",
      "Architecture design for CAD applications",
      "Performance optimization techniques",
      "Error handling and logging",
      "Testing CAD automation code",
      "Deployment and maintenance"
    ],
    tools: ["C#", "Python", "C++", "VB.NET", "Multiple CAD APIs", "Visual Studio"],
    curriculum: [
      {
        title: "Module 1: Foundations",
        topics: ["CAD API landscapes", "Common object models", "Development best practices", "Project structure"]
      },
      {
        title: "Module 2: Design Patterns",
        topics: ["Transaction patterns", "Selection handling", "Entity creation patterns", "Error management"]
      },
      {
        title: "Module 3: Cross-Platform Code",
        topics: ["Abstraction layers", "Platform-specific adapters", "Shared libraries", "Code reuse strategies"]
      },
      {
        title: "Module 4: User Interface",
        topics: ["UI frameworks", "Dialog design", "User experience", "Accessibility"]
      },
      {
        title: "Module 5: Data Integration",
        topics: ["Database connectivity", "File formats", "Web services", "PLM integration"]
      },
      {
        title: "Module 6: Professional Development",
        topics: ["Testing strategies", "Documentation", "Version control", "Team collaboration"]
      }
    ],
    projects: [
      "Multi-platform utility library",
      "Cross-CAD data exchange tool",
      "Enterprise automation framework"
    ],
    audience: ["Senior CAD developers", "Software architects", "Team leads", "Consultants"]
  },
  "rhino3d-api": {
    name: "Rhino 3D APIs",
    tagline: "Master Rhino 3D Automation & Plugin Development",
    intro: "Learn to automate Rhino 3D using RhinoCommon and Grasshopper APIs. Build custom plugins, scripts, and computational design tools using C#, Python, and RhinoScript.",
    duration: "2-3 months",
    level: "Intermediate to Advanced",
    skills: [
      "RhinoCommon API fundamentals and architecture",
      "Creating custom Rhino commands and plugins",
      "Working with geometry, curves, surfaces, and meshes",
      "Python scripting with RhinoPython",
      "Grasshopper component development",
      "Building standalone applications with Rhino.Inside",
      "Deploying and distributing Rhino plugins"
    ],
    tools: ["C#", "Python", "RhinoCommon", "Grasshopper", "RhinoScript", "Visual Studio"],
    curriculum: [
      {
        title: "Module 1: RhinoCommon Fundamentals",
        topics: ["Rhino object model", "Development environment setup", "Your first plugin", "Debugging techniques"]
      },
      {
        title: "Module 2: Geometry Operations",
        topics: ["Points, curves, and surfaces", "NURBS geometry", "Mesh operations", "Boolean operations"]
      },
      {
        title: "Module 3: RhinoPython Scripting",
        topics: ["Python basics in Rhino", "rhinoscriptsyntax library", "Automation scripts", "Batch processing"]
      },
      {
        title: "Module 4: Grasshopper Development",
        topics: ["Component anatomy", "Custom components in C#", "Python components", "Data trees"]
      },
      {
        title: "Module 5: User Interface",
        topics: ["Custom commands", "Eto.Forms dialogs", "Panels and docking", "Settings persistence"]
      },
      {
        title: "Module 6: Advanced Topics",
        topics: ["Rhino.Inside applications", "File format handling", "Performance optimization", "Deployment"]
      }
    ],
    projects: [
      "Custom geometry generator plugin",
      "Grasshopper automation component",
      "Batch file processor for Rhino"
    ],
    audience: ["Architects", "Computational designers", "3D modelers", "Software developers entering Rhino"]
  }
};

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const course = courseData[courseId];

  if (!course) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-[#0F172A]">Course not found</h1>
          <Link to="/courses" className="text-[#2563EB] mt-4 inline-block">
            ← Back to Courses
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Breadcrumb />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-block px-3 py-1 bg-[#06B6D4]/10 text-[#06B6D4] rounded-full text-sm font-medium mb-4">
                {course.level}
              </span>
              
              <h1 
                data-testid="course-title"
                className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#0F172A] leading-tight"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {course.name}
              </h1>
              
              <p className="mt-2 text-xl text-[#06B6D4] font-medium">
                {course.tagline}
              </p>
              
              <p className="mt-4 text-lg text-[#0F172A]/70 leading-relaxed">
                {course.intro}
              </p>

              <div className="flex flex-wrap items-center gap-6 mt-6 text-sm">
                <span className="flex items-center gap-2 text-[#0F172A]/70">
                  <Clock className="w-5 h-5 text-[#2563EB]" />
                  {course.duration}
                </span>
                <span className="flex items-center gap-2 text-[#0F172A]/70">
                  <Users className="w-5 h-5 text-[#2563EB]" />
                  Hands-on Training
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Button 
                  data-testid="enroll-button"
                  onClick={() => navigate("/contact")}
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-8 py-6 text-base font-medium rounded-xl"
                >
                  Enroll Now
                </Button>
                <Button 
                  data-testid="contact-button"
                  onClick={() => navigate("/contact")}
                  variant="outline"
                  className="border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white px-8 py-6 text-base font-medium rounded-xl"
                >
                  Contact Us
                </Button>
              </div>
            </div>

            <div className="hidden lg:block">
              <img
                src="https://images.pexels.com/photos/716661/pexels-photo-716661.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt={course.name}
                className="w-full h-auto rounded-3xl border border-slate-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What You Will Learn */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-[#2563EB]" />
            </div>
            <h2 
              className="text-2xl sm:text-3xl font-bold text-[#0F172A]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              What You Will Learn
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {course.skills.map((skill, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 p-4 bg-[#F8FAFC] rounded-xl"
              >
                <CheckCircle className="w-5 h-5 text-[#06B6D4] mt-0.5 flex-shrink-0" />
                <span className="text-[#0F172A]/80">{skill}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Covered */}
      <section className="py-16 lg:py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#06B6D4]/10 flex items-center justify-center">
              <Wrench className="w-6 h-6 text-[#06B6D4]" />
            </div>
            <h2 
              className="text-2xl sm:text-3xl font-bold text-[#0F172A]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Technologies & Tools
            </h2>
          </div>

          <div className="flex flex-wrap gap-3">
            {course.tools.map((tool, index) => (
              <span 
                key={index}
                className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-[#0F172A] font-medium"
              >
                {tool}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl sm:text-3xl font-bold text-[#0F172A] mb-8"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Course Curriculum
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            {course.curriculum.map((module, index) => (
              <AccordionItem 
                key={index} 
                value={`module-${index}`}
                className="bg-[#F8FAFC] border border-slate-200 rounded-xl px-6 data-[state=open]:border-[#2563EB]/30"
              >
                <AccordionTrigger 
                  data-testid={`curriculum-module-${index + 1}`}
                  className="text-left hover:no-underline py-5"
                >
                  <span 
                    className="text-lg font-semibold text-[#0F172A]"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {module.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-5">
                  <ul className="space-y-2">
                    {module.topics.map((topic, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-[#0F172A]/70">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#06B6D4]"></div>
                        {topic}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Projects */}
      <section className="py-16 lg:py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl sm:text-3xl font-bold text-[#0F172A] mb-8"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Hands-on Projects
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {course.projects.map((project, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#06B6D4]/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#2563EB]/10 flex items-center justify-center mb-4">
                  <span className="text-[#2563EB] font-bold">{index + 1}</span>
                </div>
                <p className="text-[#0F172A] font-medium">{project}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Course Is For */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 flex items-center justify-center">
              <Target className="w-6 h-6 text-[#2563EB]" />
            </div>
            <h2 
              className="text-2xl sm:text-3xl font-bold text-[#0F172A]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Who This Course Is For
            </h2>
          </div>

          <div className="flex flex-wrap gap-3">
            {course.audience.map((item, index) => (
              <span 
                key={index}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#F8FAFC] border border-slate-200 rounded-xl text-[#0F172A]/80"
              >
                <CheckCircle className="w-4 h-4 text-[#06B6D4]" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTABanner 
        title="Start Learning Today"
        subtitle="Take the first step towards mastering CAD API development. Enroll now and transform your career."
        buttonText="Enroll Now"
        buttonLink="/contact"
      />

      <Footer />
    </div>
  );
};

export default CourseDetailPage;
